import { useState } from "react";
import {
  myPageDataEnum,
  PageData,
  useWebpageData,
} from "../../hooks/useWebpageData";
import { Link, unstable_usePrompt } from "react-router-dom";
import { iconRenderMapper } from "../../util/iconMapper";
import { RenderEditImage, RenderEditItem } from "../Preview/Edit/RenderEdits";
import styles from "./SimpleData.module.css";
import { RenderSectionItems } from "../Preview/Edit/RenderItems";

interface SimpleDataProps {
  inputData: PageData;
}

export function SimpleData({ inputData }: SimpleDataProps) {
  const { myPageData, saveMyPageData } = useWebpageData(inputData);
  const [companyDataEdited, setCompanyDataEdited] = useState(false);

  // TODO: Replace with better implementation
  unstable_usePrompt({
    message: "You have unsaved changes, are you sure you want to leave?",
    when: companyDataEdited,
  });

  function companyEditedAction() {
    setCompanyDataEdited(true);
  }

  function saveCompanyWebpageData() {
    // Enable service section if items are present
    if (myPageData.get(myPageDataEnum.serviceItems).value.length > 0) {
      myPageData.get(myPageDataEnum.enableSectionTwo).setter(true);
    }
    saveMyPageData();
    setCompanyDataEdited(false);
  }

  function resetAccountInfo() {
    window.location.reload();
  }

  return (
    <div>
      {companyDataEdited && (
        <div className={styles.saveCard}>
          <button className="mainButton" onClick={saveCompanyWebpageData}>
            Save Changes
          </button>{" "}
          <button className="altButton" onClick={resetAccountInfo}>
            Reset Changes
          </button>
        </div>
      )}
      <div className={styles.companyCards}>
        <div className={styles.companyCard}>
          <div className={styles.companyCardContent}>
            <h3>Who?</h3>
            <span className={styles.companyInfoLine}>
              <p className={styles.companyInfoLineLabel}>
                {iconRenderMapper("flag")}
              </p>
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.companyName)}
                saveAction={companyEditedAction}
                placeholder="Company Name"
              />
            </span>
            <span className={styles.companyInfoLine}>
              <p className={styles.companyInfoLineLabel}>
                {iconRenderMapper("text")}
              </p>
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.companyDescription)}
                enableTextArea
                saveAction={companyEditedAction}
                placeholder="Company Description"
              />
            </span>
          </div>
        </div>
        <div className={styles.companyCard}>
          <div className={styles.companyCardContent}>
            <h3>Where?</h3>
            <span className={styles.companyInfoLine}>
              <p className={styles.companyInfoLineLabel}>
                {iconRenderMapper("location")}
              </p>
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.addressFreeForm)}
                saveAction={companyEditedAction}
                placeholder="Located / Address"
              />
            </span>
          </div>
        </div>
        <div className={styles.companyCard}>
          <div className={styles.companyCardContent}>
            <h3>Contact</h3>
            <span className={styles.companyInfoLine}>
              <p className={styles.companyInfoLineLabel}>
                {iconRenderMapper("user")}
              </p>
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.owner)}
                saveAction={companyEditedAction}
                placeholder="Owner Name"
              />
            </span>
            <span className={styles.companyInfoLine}>
              <p className={styles.companyInfoLineLabel}>
                {iconRenderMapper("email")}
              </p>
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.email)}
                saveAction={companyEditedAction}
                placeholder="Company Email"
              />
            </span>
            <span className={styles.companyInfoLine}>
              <p className={styles.companyInfoLineLabel}>
                {iconRenderMapper("phone")}
              </p>
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.phone)}
                saveAction={companyEditedAction}
                placeholder="Phone Number"
              />
            </span>
            <span className={styles.companyInfoLine}>
              <p className={styles.companyInfoLineLabel}>
                {iconRenderMapper("link")}
              </p>
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.socialUrl)}
                saveAction={companyEditedAction}
                placeholder="Social Media or External Link"
              />
            </span>
          </div>
        </div>
        <div
          className={styles.companyCard}
          style={{
            backgroundColor: myPageData.get(myPageDataEnum.primaryColor).value,
            color: myPageData.get(myPageDataEnum.secondaryColor).value,
          }}
        >
          <div className={styles.companyCardContent}>
            <h3>Style</h3>
            <span className={styles.companyInfoLine}>
              <span className={styles.companyInfoLineLabel}>
                {iconRenderMapper("flag-fill")}
                <p>Primary Color</p>
              </span>
              <input
                className={styles.companyInputColor}
                type="color"
                value={myPageData.get(myPageDataEnum.primaryColor).value}
                onChange={e =>
                  myPageData
                    .get(myPageDataEnum.primaryColor)
                    .setter(e.currentTarget.value)
                }
                onClick={companyEditedAction}
              />
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.primaryColor)}
                saveAction={companyEditedAction}
              />
            </span>
            <span className={styles.companyInfoLine}>
              <span className={styles.companyInfoLineLabel}>
                {iconRenderMapper("flag-fill")}
                <p>Secondary Color</p>
              </span>
              <input
                className={styles.companyInputColor}
                type="color"
                value={myPageData.get(myPageDataEnum.secondaryColor).value}
                onChange={e =>
                  myPageData
                    .get(myPageDataEnum.secondaryColor)
                    .setter(e.currentTarget.value)
                }
                onClick={companyEditedAction}
              />
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.secondaryColor)}
                saveAction={companyEditedAction}
              />
            </span>
            <span className={styles.companyInfoLine}>
              <p className={styles.companyInfoLineLabel}>
                {iconRenderMapper("flag-alt")} Logo
              </p>
            </span>
            <span className={styles.companyInfoLine}>
              <div className={styles.companyInfoImg}>
                <RenderEditImage
                  inputSrc={myPageData.get(myPageDataEnum.logo)}
                  altText="logo"
                  saveAction={companyEditedAction}
                />
              </div>
            </span>
          </div>
        </div>
        <div className={styles.companyCard}>
          <div className={styles.companyCardContent}>
            <h3>
              <RenderEditItem
                inputItem={myPageData.get(myPageDataEnum.serviceItemsTitle)}
                saveAction={companyEditedAction}
                placeholder="Title for Service Items"
              />
            </h3>
            <div>
              <RenderSectionItems
                inputItems={myPageData.get(myPageDataEnum.serviceItems)}
                editModeEnabled={true}
                enableImgs
                itemClass="companyCardItems"
                groupClass="companyCardItemsGroup"
                saveAction={companyEditedAction}
              />
            </div>
            <p>
              Go to <Link to="/webpage#top">Webpage</Link> to see your
              information reflected with style.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
